import { useCallback, useEffect, useState } from "react";

/** How long the "copied" confirmation stays before the button returns to its resting state. */
const COPIED_RESET_MS = 2000;

export interface UseCodeBlockResult {
  /** True for a moment after a successful copy. */
  copied: boolean;
  /** Puts the code on the clipboard; stays silent if the browser refuses. */
  copy: () => void;
}

/** Copy-to-clipboard behaviour for a code block, with a short-lived "copied" confirmation. */
export function useCodeBlock(code: string): UseCodeBlockResult {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(code).then(
      () => setCopied(true),
      () => {}
    );
  }, [code]);

  return { copied, copy };
}
